import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
function _getWindow(): any {
  return window;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleTagManagerService {
  private window: any;
  private lastLoggedUrl: string | null = null;
  private readonly googleTagId = (environment.googleTagId || '').trim();
  private readonly isGtmContainer = this.googleTagId.startsWith('GTM-');

  constructor() {
    this.window = _getWindow();
    this.initialize();
  }

  private initialize() {
    if (!this.window || !this.googleTagId || this.googleTagId === 'undefined') return;

    if (!Array.isArray(this.window.dataLayer)) {
      this.window.dataLayer = [];
    }

    const scriptId = this.isGtmContainer ? 'gtm-script' : 'gtag-script';

    if (this.isGtmContainer) {
      this.window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    } else {
      const dataLayer = this.window.dataLayer;
      this.window.gtag =
        this.window.gtag ||
        function () {
          dataLayer.push(arguments);
        };

      this.window.gtag('js', new Date());
      // Router NavigationEnd is the single source of page views; the automatic hit would double count.
      this.window.gtag('config', this.googleTagId, { send_page_view: false });
    }

    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.src = this.isGtmContainer
        ? `https://www.googletagmanager.com/gtm.js?id=${this.googleTagId}`
        : `https://www.googletagmanager.com/gtag/js?id=${this.googleTagId}`;

      document.head.appendChild(script);
    }
  }

  private pingHome(obj: Record<string, unknown>) {
    if (obj && this.window && Array.isArray(this.window.dataLayer)) this.window.dataLayer.push(obj);
  }

  logPageView(url: string) {
    if (!this.googleTagId || this.googleTagId === 'undefined') return;

    // the router can emit consecutive NavigationEnd events for one user-visible navigation
    if (url === this.lastLoggedUrl) return;
    this.lastLoggedUrl = url;

    if (!this.isGtmContainer && this.window?.gtag) {
      // update:true merges page context into the existing config without emitting its own page_view.
      // page_referrer is deliberately omitted so GA fills in the previous virtual page.
      this.window.gtag('config', this.googleTagId, {
        page_location: this.window.location?.href ?? url,
        page_title: document.title,
        update: true,
      });
      this.window.gtag('event', 'page_view');
      return;
    }

    const hit = {
      event: 'content-view',
      pageName: url,
    };
    this.pingHome(hit);
  }

  logEvent(event: string, category: string, action: string) {
    if (!this.googleTagId || this.googleTagId === 'undefined') return;

    if (!this.isGtmContainer && this.window?.gtag) {
      this.window.gtag('event', event, {
        event_category: category,
        event_action: action,
      });
      return;
    }

    const hit = {
      event,
      category,
    };
    this.pingHome(hit);
  }
}
